import { Button, Checkbox, Col, Input, List, Modal, Row, Tag } from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { IoCloseCircleOutline } from "react-icons/io5";
import apiCall from "../../../components/Api";
import ExcelDownload from "../../../components/Utils/excelDownload";
import StatusComponent from "../../../components/Common/StatusComponent";
import { prescriptionType } from "../../../components/Constants";
import medicine from "../../../assets/images/medicine-image.png";

export const getSrcValue = (imgPath = "", imgName = "") => {
  if (typeof imgName === "string" && imgName.length)
    return process.env.REACT_APP_API_URL + imgPath + "/" + imgName;
};

export const breadcrumbsList = [
  {
    text: "HealthSy Services",
    link: "/healthsy-services/order-medicines/medicine-details",
  },
  {
    text: "Order Medicines",
    link: "/healthsy-services/order-medicines/medicine-details",
  },
  { text: "Medicine List", link: "" },
];

export const formatUpdatedAt = (timestamp) => {
  return dayjs(timestamp).format("DD-MM-YYYY | hh:mm A");
};

export const fetchMedicineList = async ({
  skip = 0,
  limit = 10,
  sort = {},
  searchText = "",
  filterValues = null,
}) => {
  let modifiedFilter = {
    skip,
    limit: Number(limit),
    sort,
    searchText,
  };

  if (filterValues) {
    modifiedFilter = {
      molecule_name: filterValues?.molecule_name?.map((item) => item._id) || [],
      manufacturer_name:
        filterValues?.manufacturer_name?.map((item) => item._id) || [],
      product_type_name:
        filterValues?.product_type_name?.map((item) => item._id) || [],
      product_id: filterValues?.product_id?.map((item) => item._id) || [],
      discount:
        filterValues?.discount?.map((d) => parseFloat(d.replace("%", ""))) ||
        [],
      prescription_type:
        filterValues?.prescription_type?.length === 1
          ? [filterValues?.prescription_type[0] === "Rx"]
          : [],
      variant: filterValues?.variant?.length === 1 ? filterValues?.variant : [],
      medicine_status:
        filterValues?.medicine_status?.length === 1
          ? [filterValues?.medicine_status[0] === "Active"]
          : [],
      skip,
      limit: Number(limit),
      sort,
      searchText,
    };
  }

  try {
    const response = await apiCall(`medicine/list`, "POST", modifiedFilter);
    return response.data;
  } catch (err) {
    console.error("Error in fetchMedicineList:", err);
    throw err;
  }
};

export const fetchManufacturerList = async () => {
  try {
    const response = await apiCall(`manufacturer/list`, "POST", {
      active: true,
      projectionFields: {
        manufacturer_name: 1,
      },
    });
    return response.data?.records || [];
  } catch (err) {
    console.error("Failed to fetch manufacturer list", err);
    throw new Error("Failed to fetch manufacturers");
  }
};

export const fetchMedicineTypes = async () => {
  try {
    const response = await apiCall(`product-type/list`, "POST", {
      active: true,
      projectionFields: {
        product_type_name: 1,
      },
    });
    return response.data?.records || [];
  } catch (err) {
    console.error("Failed to fetch medicine types", err);
    throw new Error("Failed to fetch medicine types");
  }
};

export const fetchSaltList = async () => {
  try {
    const response = await apiCall(`salt-molecule/list`, "POST", {
      active: true,
      projectionFields: {
        molecule_name: 1,
      },
    });
    return response.data?.records || [];
  } catch (err) {
    console.error("Failed to fetch salt list", err);
    throw new Error("Failed to fetch salt list");
  }
};

export const NewFilterModal = ({
  visible,
  onClose,
  filterOptions,
  appliedFilter,
  onApplyFilters = () => {},
  defaultValue = "",
  filterKeyValues,
}) => {
  const [selectedFilter, setSelectedFilter] = useState(defaultValue);
  const [selectedValues, setSelectedValues] = useState(appliedFilter);
  const [search, setSearch] = useState("");

  const handleSelectFilter = (filter) => {
    setSelectedFilter(filter);
    setSearch("");
  };
  const handleCheckboxChange = (item) => {
    setSelectedValues((prev) => {
      const newValues = { ...prev };
      if (typeof item === "string") {
        if (newValues[selectedFilter]?.includes(item)) {
          newValues[selectedFilter] = newValues[selectedFilter].filter(
            (selected) => selected !== item,
          );
        } else {
          newValues[selectedFilter] = [
            ...(newValues[selectedFilter] || []),
            item,
          ];
        }
      } else {
        if (
          newValues[selectedFilter]?.some(
            (selected) => selected._id === item._id,
          )
        ) {
          newValues[selectedFilter] = newValues[selectedFilter].filter(
            (selected) => selected._id !== item._id,
          );
        } else {
          newValues[selectedFilter] = [
            ...(newValues[selectedFilter] || []),
            item,
          ];
        }
      }
      return newValues;
    });
  };

  const handleClearAll = () => {
    setSelectedValues({});
  };

  const handleRemoveItem = (filter, value) => {
    setSelectedValues((prev) => {
      if (typeof value === "string") {
        return {
          ...prev,
          [filter]: prev[filter].filter((item) => item !== value),
        };
      }
      return {
        ...prev,
        [filter]: prev[filter].filter((item) => item._id !== value._id),
      };
    });
  };

  const handleApplyFilters = () => {
    onApplyFilters(selectedValues);
    onClose();
  };

  const searchOption =
    Array.isArray(filterOptions[selectedFilter]) &&
    typeof filterOptions[selectedFilter][0] === "object";

  const filteredOptions = searchOption
    ? filterOptions[selectedFilter]?.filter((item) =>
        item[selectedFilter]?.toLowerCase().includes(search?.toLowerCase()),
      )
    : filterOptions[selectedFilter];

  const filtersCount = useMemo(
    () =>
      Object.keys(selectedValues).filter(
        (keyName) => selectedValues?.[keyName]?.length,
      )?.length,
    [selectedValues],
  );
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      className="medicineFilterModal"
      closeIcon={<IoCloseCircleOutline size={24} />}
      centered
      footer={null}
      width={800}
      style={{
        maxHeight: 500,
      }}
      title="Filters"
    >
      <Row gutter={16}>
        <Col span={6}>
          <List
            dataSource={Object.keys(filterOptions)}
            renderItem={(item) => (
              <List.Item
                className={`cursor-pointer ${
                  selectedFilter === item ? "seletedItem" : ""
                }`}
                style={{
                  cursor: "pointer",
                  fontWeight: selectedFilter === item ? "bold" : "normal",
                }}
                onClick={() => handleSelectFilter(item)}
              >
                {filterKeyValues[item]}
              </List.Item>
            )}
          />
        </Col>
        <Col
          span={10}
          style={{
            maxHeight: 400,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {searchOption && (
            <div
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 10,
                padding: "8px",
              }}
            >
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <List
            dataSource={filteredOptions}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={
                    typeof item === "string"
                      ? selectedValues[selectedFilter]?.includes(item)
                      : selectedValues[selectedFilter]?.some(
                          (selected) => selected._id === item._id,
                        )
                  }
                  onChange={() => handleCheckboxChange(item)}
                >
                  {typeof item === "string" ? item : item[selectedFilter]}
                </Checkbox>
              </List.Item>
            )}
          />
        </Col>
        <Col
          span={8}
          style={{
            maxHeight: 400,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div>
            <strong>{filtersCount} Filters Applied</strong>
            <Button type="link" onClick={handleClearAll}>
              Clear All
            </Button>
          </div>
          {Object.keys(filterOptions).map((filter) => (
            <div key={filter}>
              {selectedValues[filter]?.length > 0 && (
                <div className="mt-2 fs14fw500">{filterKeyValues[filter]}</div>
              )}
              {selectedValues[filter]?.map((value) => (
                <Tag
                  key={`${filter}-${
                    typeof value === "string" ? value : value._id
                  }`}
                  closable
                  closeIcon={<IoMdClose size={17} />}
                  onClose={() => handleRemoveItem(filter, value)}
                  className="text-wrap fs14fw500 m-2 px-2 py-1 border-0"
                  style={{
                    backgroundColor: "#FAE8EF",
                  }}
                >
                  {typeof value === "string" ? value : value[filter]}
                </Tag>
              ))}
            </div>
          ))}
        </Col>
      </Row>
      <div style={{ textAlign: "right", marginTop: 16 }}>
        {/* <Button onClick={onClose} style={{ marginRight: 8 }}>Close</Button> */}
        <Button onClick={handleClearAll} className="me-2">
          Reset Filters
        </Button>
        <Button type="primary" onClick={handleApplyFilters}>
          Apply
        </Button>
      </div>
    </Modal>
  );
};

export const DetailRow = ({ data = [] }) => (
  <Row gutter={[16, 20]}>
    {data.map((item, index) => (
      <React.Fragment key={index}>
        <Col className="fs16fw600" span={8}>
          <span style={{ color: "#4D4D4D" }}>{item.label}</span>
        </Col>
        <Col className="fs16fw600" span={16}>
          <span className="text-black">{item.value}</span>
        </Col>
      </React.Fragment>
    ))}
  </Row>
);

export const Section = ({ title, children, isLast = false }) => (
  <>
    <h6 className="text-primarycolor fs18 fw-bold my-3">{title}</h6>
    {children}
    {!isLast && (
      <hr className="my-4" style={{ border: "0.5px solid #7d7d7d" }} />
    )}
  </>
);

export const downloadMedicineList = async ({
  appliedFilter,
  sort,
  searchText,
  prescriptionType,
  returnable,
}) => {
  const filterOptions = {
    skip: 0,
    limit: 3000,
    sort,
    searchText,
  };

  if (appliedFilter?.molecule_name?.length) {
    filterOptions.molecule_name = appliedFilter.molecule_name.map(
      (item) => item._id,
    );
  }

  if (appliedFilter?.manufacturer_name?.length) {
    filterOptions.manufacturer_name = appliedFilter.manufacturer_name.map(
      (item) => item._id,
    );
  }

  if (appliedFilter?.product_type_name?.length) {
    filterOptions.product_type_name = appliedFilter.product_type_name.map(
      (item) => item._id,
    );
  }

  if (appliedFilter?.discount?.length) {
    filterOptions.discount = appliedFilter.discount.map((d) =>
      parseFloat(d.replace("%", "")),
    );
  }

  if (appliedFilter?.prescription_type?.length === 1) {
    filterOptions.prescription_type = [
      appliedFilter.prescription_type[0] === "Rx",
    ];
  }

  if (appliedFilter?.variant?.length === 1) {
    filterOptions.variant = appliedFilter.variant;
  }

  if (appliedFilter?.medicine_status?.length === 1) {
    filterOptions.medicine_status = [
      appliedFilter.medicine_status[0] === "Active",
    ];
  }

  try {
    const response = await apiCall(`medicine/list`, "POST", filterOptions);
    const records = response.data?.rows;

    if (!records?.length) {
      throw new Error("No records available to download.");
    }

    const formattedRecords = records.map((record, index) => ({
      "S.No": index + 1,
      "Medicine Name": record.medicine_name || "",
      Type: record?.medicine_type?.product_type_name || "",
      MRP: "₹" + record?.mrp,
      "Manufacture Name": record?.manufacturer_name?.manufacturer_name || "",
      "Package Description ": record?.package_description || "",
      "Discount Percentage ": record.discount + "%",
      "Prescription Mandate":
        record.prescription_type === true
          ? prescriptionType?.[0]
          : prescriptionType?.[1],
      Returnable:
        record.returnable === true ? returnable?.[0] : returnable?.[1],
      "Molecule Name": record.salt_molecule?.molecule_name || "",
    }));

    ExcelDownload(formattedRecords, "", "InClinic-Partnered-doctor.xlsx");
  } catch (err) {
    console.error("Download error:", err);
    throw err;
  }
};

export const getMedicineTableColumns = ({
  skip,
  showPopup,
  setShowPopup,
  handleEdit,
  setDeleteModal,
  handleActive,
  setshowMedicineDetails,
  setMedicineDetails,
}) => [
  {
    title: "S.No",
    render: (text, record, index) => Number(skip || 0) + (index + 1),
    fixed: "left",
    key: "serial",
    width: 80,
  },
  {
    title: "Status & Actions",
    render: (text, record) => (
      <StatusComponent
        text={text}
        record={record}
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        handleEdit={handleEdit}
        setDeleteModal={setDeleteModal}
        handleActive={handleActive}
        title={""}
        active="medicine_url_status"
      />
    ),
    fixed: "left",
    key: "actions",
    width: 200,
  },
  {
    title: "Medicine Name",
    dataIndex: "medicine_name",
    fixed: "left",
    key: "medicine_name",
    width: 200,
    render: (text, record) => (
      <span
        className="text-primarycolor text-capitalize fw-bold cursor-pointer"
        onClick={() => {
          setMedicineDetails(record);
          setshowMedicineDetails(true);
        }}
      >
        {record.medicine_name}
      </span>
    ),
  },
  {
    title: "Product ID",
    dataIndex: "product_id",
    fixed: "left",
    key: "Product_id",
    width: 150,
    render: (text, record) => <span>{record.product_id}</span>,
  },
  {
    title: "Image",
    dataIndex: "product_type_name",
    key: "product_type_name_img",
    width: 200,
    render: (text, record) => (
      <img
        src={getSrcValue("medicines", record?.photos?.[0] || medicine)}
        className="doctorProfileImage"
        // alt={"Medicine"}
      />
    ),
  },

  {
    title: "Medicine Status",
    dataIndex: "Medicine_status",
    key: "medicine_status",
    width: 200,
    render: (text, record) => (
      <span className="">{record.medicine_status}</span>
    ),
  },
  {
    title: "Manufacturer Name",
    dataIndex: "manufacturer_name",
    key: "manufacturer_name",
    render: (text, record) => (
      <span>{record.manufacturer_name?.manufacturer_name}</span>
    ),
  },
  {
    title: "MRP",
    dataIndex: "mrp_per_unit",
    key: "mrp_per_unit",
    render: (text, record) => <span>₹{record.mrp}</span>,
  },
  {
    title: "Discount",
    dataIndex: "discount",
    key: "discount",
    render: (text, record) => <span>{record.discount}%</span>,
  },
  {
    title: "Molecule",
    dataIndex: "salt_molecule",
    key: "salt_molecule",
    render: (text, record) => (
      <span>{record.salt_molecule?.molecule_name}</span>
    ),
  },
  {
    title: "Prescription Type",
    dataIndex: "prescription_type",
    key: "prescription_type",
    width: 200,
    render: (text, record) => (
      <span>
        {record.prescription_type === true
          ? prescriptionType[0]
          : prescriptionType[1]}
      </span>
    ),
  },
  {
    title: "Last Updated On",
    dataIndex: "updatedAt",
    key: "updatedAt",
    render: (text, record) => <span>{formatUpdatedAt(record.updatedAt)}</span>,
  },
  {
    title: "Sitemap Webpages Status",
    dataIndex: "product_type_name",
    key: "sitemap_status",
    width: 350,
    render: (text, record) => <span>{record.product_type_name}</span>,
  },
  {
    title: "Webpage Indexation Status",
    dataIndex: "product_type_name",
    key: "web_index_status",
    width: 350,
    render: (text, record) => <span>{record.product_type_name}</span>,
  },
];

export const usePaginatedList = ({
  endpoint,
  initialSort = "newest",
  defaultLimit = 10,
}) => {
  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [sort, setSort] = useState(initialSort);
  const [searchText, setSearchText] = useState("");

  const pages = Math.ceil(count / limit);

  const list_api_call = useCallback(async () => {
    try {
      const response = await apiCall(endpoint, "POST", {
        skip,
        limit,
        sort,
        searchText,
      });
      setRecords(response.data.records);
      setCount(response.data.count);
    } catch (error) {
      console.error(error);
    }
  }, [skip, limit, count, sort, searchText, endpoint]);

  useEffect(() => {
    list_api_call();
  }, [list_api_call]);

  const handlePageChange = (event) => setSkip(event.selected * limit);
  const handleSortClick = (selectedSort) => setSort(selectedSort);
  const debouncedSearch = debounce((value) => {
    setSkip(0);
    setSearchText(value);
  }, 500);

  return {
    records,
    count,
    pages,
    skip,
    limit,
    setLimit,
    handlePageChange,
    handleSortClick,
    debouncedSearch,
    list_api_call,
  };
};
